/**
 * Niche-based presets — single source of truth for what each business niche
 * needs by default. Used by SiteVisitFieldNotes (measurements, photo tags,
 * checklist), EstimateWizard (service menu), and Settings (recurring toggles).
 *
 * The org's `business_type` on its document drives which preset applies.
 * Mappings:
 *   construction (default) | concrete | roofing | fencing | decking | painting | plumbing | electrical | hvac
 *   lawn_care | landscaping
 *   detailing
 *   cleaning
 */

export const NICHE_LABELS = {
  construction: 'General Construction',
  concrete: 'Concrete',
  roofing: 'Roofing',
  fencing: 'Fencing',
  decking: 'Decking',
  painting: 'Painting',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  hvac: 'HVAC',
  lawn_care: 'Lawn Care',
  landscaping: 'Landscaping',
  detailing: 'Vehicle Detailing',
  cleaning: 'Cleaning',
  general_contractor: 'General Contractor',
};

/**
 * Map a free-form business_type / industry / service_type to a canonical niche key.
 */
export const resolveNiche = (input) => {
  if (!input) return 'construction';
  const s = String(input).toLowerCase().trim().replace(/\s+/g, '_');
  if (s.includes('lawn')) return 'lawn_care';
  if (s.includes('landscap')) return 'landscaping';
  if (s.includes('detail') || s.includes('auto') || s.includes('car_wash')) return 'detailing';
  if (s.includes('clean') || s.includes('janitor') || s.includes('maid')) return 'cleaning';
  if (s.includes('concrete')) return 'concrete';
  if (s.includes('roof')) return 'roofing';
  if (s.includes('fenc')) return 'fencing';
  if (s.includes('deck')) return 'decking';
  if (s.includes('paint')) return 'painting';
  if (s.includes('plumb')) return 'plumbing';
  if (s.includes('elec')) return 'electrical';
  if (s.includes('hvac') || s.includes('heating') || s.includes('cooling')) return 'hvac';
  return 'construction';
};

/**
 * Photo tag presets by niche. Each tag: {value, label, color}
 */
export const PHOTO_TAGS_BY_NICHE = {
  construction: [
    { value: 'before', label: 'Before', color: 'bg-blue-100 text-blue-700' },
    { value: 'progress', label: 'Progress', color: 'bg-amber-100 text-amber-700' },
    { value: 'after', label: 'After', color: 'bg-green-100 text-green-700' },
    { value: 'damage', label: 'Damage', color: 'bg-red-100 text-red-700' },
    { value: 'measurement', label: 'Measurement', color: 'bg-purple-100 text-purple-700' },
    { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' },
  ],
  lawn_care: [
    { value: 'before_mow', label: 'Before Mow', color: 'bg-blue-100 text-blue-700' },
    { value: 'after_mow', label: 'After Mow', color: 'bg-green-100 text-green-700' },
    { value: 'problem_area', label: 'Problem Area', color: 'bg-red-100 text-red-700' },
    { value: 'beds', label: 'Garden Beds', color: 'bg-emerald-100 text-emerald-700' },
    { value: 'gate_access', label: 'Gate / Access', color: 'bg-amber-100 text-amber-700' },
    { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' },
  ],
  landscaping: [
    { value: 'before', label: 'Before', color: 'bg-blue-100 text-blue-700' },
    { value: 'progress', label: 'Progress', color: 'bg-amber-100 text-amber-700' },
    { value: 'after', label: 'After', color: 'bg-green-100 text-green-700' },
    { value: 'plants', label: 'Plants', color: 'bg-emerald-100 text-emerald-700' },
    { value: 'irrigation', label: 'Irrigation', color: 'bg-cyan-100 text-cyan-700' },
    { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' },
  ],
  detailing: [
    { value: 'exterior_before', label: 'Exterior Before', color: 'bg-blue-100 text-blue-700' },
    { value: 'exterior_after', label: 'Exterior After', color: 'bg-green-100 text-green-700' },
    { value: 'interior_before', label: 'Interior Before', color: 'bg-indigo-100 text-indigo-700' },
    { value: 'interior_after', label: 'Interior After', color: 'bg-emerald-100 text-emerald-700' },
    { value: 'wheels', label: 'Wheels', color: 'bg-slate-100 text-slate-700' },
    { value: 'engine_bay', label: 'Engine Bay', color: 'bg-orange-100 text-orange-700' },
    { value: 'damage', label: 'Damage / Issue', color: 'bg-red-100 text-red-700' },
  ],
  cleaning: [
    { value: 'before', label: 'Before', color: 'bg-blue-100 text-blue-700' },
    { value: 'after', label: 'After', color: 'bg-green-100 text-green-700' },
    { value: 'problem', label: 'Problem Area', color: 'bg-red-100 text-red-700' },
    { value: 'access', label: 'Access / Key', color: 'bg-amber-100 text-amber-700' },
    { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' },
  ],
};

/**
 * Default field-notes checklist by niche.
 */
export const CHECKLIST_BY_NICHE = {
  construction: [
    { id: 'site_access', text: 'Site access confirmed', checked: false },
    { id: 'measurements', text: 'Measurements taken', checked: false },
    { id: 'photos', text: 'Photos captured', checked: false },
    { id: 'customer_requirements', text: 'Customer requirements discussed', checked: false },
    { id: 'permits', text: 'Permits / inspections noted', checked: false },
  ],
  lawn_care: [
    { id: 'gate_code', text: 'Gate code / access verified', checked: false },
    { id: 'pets', text: 'Pets noted (dog, etc.)', checked: false },
    { id: 'sprinklers', text: 'Sprinkler heads / flags marked', checked: false },
    { id: 'measurements', text: 'Lawn area measured', checked: false },
    { id: 'before_after_photos', text: 'Before/after photos taken', checked: false },
  ],
  landscaping: [
    { id: 'site_access', text: 'Site access confirmed', checked: false },
    { id: 'measurements', text: 'Beds / lawn measured', checked: false },
    { id: 'utilities', text: 'Underground utilities marked', checked: false },
    { id: 'plant_list', text: 'Plant list reviewed', checked: false },
    { id: 'photos', text: 'Photos captured', checked: false },
  ],
  detailing: [
    { id: 'vehicle_walkaround', text: 'Vehicle walk-around / damage noted', checked: false },
    { id: 'before_photos', text: 'Before photos (interior/exterior/wheels)', checked: false },
    { id: 'package_confirmed', text: 'Service package confirmed with customer', checked: false },
    { id: 'pickup_window', text: 'Pickup window confirmed', checked: false },
    { id: 'after_photos', text: 'After photos taken', checked: false },
  ],
  cleaning: [
    { id: 'access', text: 'Access / key / code confirmed', checked: false },
    { id: 'walkthrough', text: 'Walkthrough done with customer', checked: false },
    { id: 'special_areas', text: 'Special areas / instructions noted', checked: false },
    { id: 'before_photos', text: 'Before photos taken', checked: false },
    { id: 'after_photos', text: 'After photos taken', checked: false },
  ],
};

/**
 * Default site conditions per niche (extends/overrides the generic list).
 */
export const SITE_CONDITIONS_BY_NICHE = {
  construction: [
    { id: 'limited_access', label: 'Limited equipment access' },
    { id: 'demolition', label: 'Requires demolition' },
    { id: 'structure_removal', label: 'Existing structure removal' },
    { id: 'uneven_terrain', label: 'Uneven terrain' },
    { id: 'utility_lines', label: 'Utility lines present' },
    { id: 'permit_required', label: 'Permit required' },
  ],
  lawn_care: [
    { id: 'sloped_yard', label: 'Sloped or hilly' },
    { id: 'pet_present', label: 'Pet present' },
    { id: 'gate_locked', label: 'Gate locked / code needed' },
    { id: 'sprinklers', label: 'Active sprinkler system' },
    { id: 'tight_access', label: 'Tight gate / access' },
    { id: 'narrow_paths', label: 'Narrow paths / steps' },
  ],
  detailing: [
    { id: 'mobile_service', label: 'Mobile service (we travel to vehicle)' },
    { id: 'water_access', label: 'Water hookup available' },
    { id: 'power_access', label: 'Power outlet available' },
    { id: 'pet_hair', label: 'Heavy pet hair' },
    { id: 'smoker', label: 'Smoker (deep cleaning needed)' },
    { id: 'oversized', label: 'Oversized vehicle' },
  ],
  cleaning: [
    { id: 'pet_present', label: 'Pet present' },
    { id: 'children_present', label: 'Children present' },
    { id: 'allergies', label: 'Allergies / fragrance-free needed' },
    { id: 'high_traffic', label: 'High-traffic spaces' },
    { id: 'special_surfaces', label: 'Special surfaces (marble/wood/etc.)' },
  ],
};

/**
 * Niche-specific feature defaults (used by Settings / org init).
 */
export const FEATURE_DEFAULTS_BY_NICHE = {
  construction: { recurring_jobs_enabled: false, route_planning_enabled: false, vehicle_profiles: false, multi_phase: true, change_orders: true },
  concrete: { recurring_jobs_enabled: false, route_planning_enabled: false, vehicle_profiles: false, multi_phase: false, change_orders: true },
  roofing: { recurring_jobs_enabled: false, route_planning_enabled: false, vehicle_profiles: false, multi_phase: false, change_orders: true },
  lawn_care: { recurring_jobs_enabled: true, route_planning_enabled: true, vehicle_profiles: false, multi_phase: false, change_orders: false },
  landscaping: { recurring_jobs_enabled: true, route_planning_enabled: true, vehicle_profiles: false, multi_phase: true, change_orders: true },
  detailing: { recurring_jobs_enabled: true, route_planning_enabled: true, vehicle_profiles: true, multi_phase: false, change_orders: false },
  cleaning: { recurring_jobs_enabled: true, route_planning_enabled: true, vehicle_profiles: false, multi_phase: false, change_orders: false },
};

/**
 * Return the resolved preset bundle for a niche.
 */
export const getNichePreset = (nicheOrBusinessType) => {
  const niche = resolveNiche(nicheOrBusinessType);
  return {
    niche,
    label: NICHE_LABELS[niche] || NICHE_LABELS.construction,
    photoTags: PHOTO_TAGS_BY_NICHE[niche] || PHOTO_TAGS_BY_NICHE.construction,
    checklist: CHECKLIST_BY_NICHE[niche] || CHECKLIST_BY_NICHE.construction,
    siteConditions: SITE_CONDITIONS_BY_NICHE[niche] || SITE_CONDITIONS_BY_NICHE.construction,
    features: FEATURE_DEFAULTS_BY_NICHE[niche] || FEATURE_DEFAULTS_BY_NICHE.construction,
  };
};

/**
 * Merge presets across a primary niche + secondary niches.
 * Primary drives photo tags / checklist / site conditions / features.
 * Secondary niches CONTRIBUTE additional photo tags + site conditions
 * (deduped by `value` / `id`) so multi-service orgs see all relevant options.
 */
export const getMergedNichePreset = (primary, secondaries = []) => {
  const primaryPreset = getNichePreset(primary);
  if (!secondaries || !secondaries.length) {
    return { ...primaryPreset, niches: [primaryPreset.niche], allNiches: [primaryPreset.niche] };
  }
  const secondaryPresets = secondaries.map((s) => getNichePreset(s));
  const allNiches = [primaryPreset.niche, ...secondaryPresets.map((p) => p.niche)];
  const uniqueNiches = Array.from(new Set(allNiches));

  // Merge photo tags (dedupe by value)
  const photoTagSeen = new Set(primaryPreset.photoTags.map((t) => t.value));
  const mergedPhotoTags = [...primaryPreset.photoTags];
  for (const p of secondaryPresets) {
    for (const tag of p.photoTags) {
      if (!photoTagSeen.has(tag.value)) {
        mergedPhotoTags.push(tag);
        photoTagSeen.add(tag.value);
      }
    }
  }

  // Merge site conditions (dedupe by id)
  const condSeen = new Set(primaryPreset.siteConditions.map((c) => c.id));
  const mergedConds = [...primaryPreset.siteConditions];
  for (const p of secondaryPresets) {
    for (const c of p.siteConditions) {
      if (!condSeen.has(c.id)) {
        mergedConds.push(c);
        condSeen.add(c.id);
      }
    }
  }

  return {
    ...primaryPreset,
    photoTags: mergedPhotoTags,
    siteConditions: mergedConds,
    niches: uniqueNiches,
    allNiches: uniqueNiches,
  };
};

/**
 * Map niche → MEASUREMENT_SUGGESTIONS service-type key.
 * Used to combine measurement object suggestions across primary + secondary niches.
 */
export const NICHE_TO_SERVICE_KEY = {
  lawn_care: 'Lawn Care',
  landscaping: 'Landscaping',
  detailing: 'Detailing',
  cleaning: 'Cleaning',
  concrete: 'Concrete',
  roofing: 'Roofing',
  fencing: 'Fencing',
  decking: 'Decking',
  painting: 'Painting',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  hvac: 'HVAC',
};
